import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'

/**
 * Every aggregation in the app happens in this zone. Timestamps are stored as UTC
 * in Firestore and only ever converted for display and bucketing.
 */
export const APP_TIME_ZONE = 'Europe/Lisbon'

/** A logical day, keyed by the calendar date it started on: 'yyyy-MM-dd'. */
export type DayKey = string

export const DEFAULT_DAY_START_HOUR = 4

const HOUR_MS = 60 * 60 * 1000

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

/** Calendar arithmetic on a day key, independent of any zone or DST. */
export function shiftDayKey(key: DayKey, days: number): DayKey {
  const [y, m, d] = key.split('-').map(Number)
  const t = Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + days)
  return formatUtcDate(new Date(t))
}

function formatUtcDate(date: Date): DayKey {
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`
}

/** Whole calendar days from `a` to `b`, negative when `b` is earlier. */
export function daysBetween(a: DayKey, b: DayKey): number {
  return Math.round((utcOf(b) - utcOf(a)) / (24 * 60 * 60 * 1000))
}

function utcOf(key: DayKey): number {
  const [y, m, d] = key.split('-').map(Number)
  return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)
}

/** 0 = Monday … 6 = Sunday. */
export function weekdayOf(key: DayKey): number {
  const [y, m, d] = key.split('-').map(Number)
  const dow = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay()
  return (dow + 6) % 7
}

/**
 * The instant a logical day begins.
 *
 * `dayStartHour` is wall-clock local, so on a DST day this lands on a different
 * UTC offset than the day before — which is exactly what makes 23h/25h days work.
 */
export function dayStartInstant(
  key: DayKey,
  dayStartHour: number = DEFAULT_DAY_START_HOUR,
  timeZone: string = APP_TIME_ZONE,
): Date {
  return fromZonedTime(`${key} ${pad2(dayStartHour)}:00:00`, timeZone)
}

/** Half-open interval [start, end) for a logical day. */
export function dayRange(
  key: DayKey,
  dayStartHour: number = DEFAULT_DAY_START_HOUR,
  timeZone: string = APP_TIME_ZONE,
): { start: Date; end: Date } {
  return {
    start: dayStartInstant(key, dayStartHour, timeZone),
    end: dayStartInstant(shiftDayKey(key, 1), dayStartHour, timeZone),
  }
}

/**
 * The instant a wall-clock time lands on, on the same calendar date as
 * `reference` in the app's zone. The block editor parses typed times with this,
 * so "14:30" means half past two in Lisbon whatever the browser's own zone is.
 */
export function instantAtClock(
  reference: number,
  hours: number,
  minutes: number,
  timeZone: string = APP_TIME_ZONE,
): number {
  const date = formatInTimeZone(new Date(reference), timeZone, 'yyyy-MM-dd')
  return fromZonedTime(`${date} ${pad2(hours)}:${pad2(minutes)}:00`, timeZone).getTime()
}

/** Real elapsed length of a logical day — 23h, 24h or 25h. */
export function dayLengthMs(
  key: DayKey,
  dayStartHour: number = DEFAULT_DAY_START_HOUR,
  timeZone: string = APP_TIME_ZONE,
): number {
  const { start, end } = dayRange(key, dayStartHour, timeZone)
  return end.getTime() - start.getTime()
}

/** Which logical day an instant belongs to. A 02:00 block belongs to yesterday. */
export function dayKeyOf(
  instant: Date,
  dayStartHour: number = DEFAULT_DAY_START_HOUR,
  timeZone: string = APP_TIME_ZONE,
): DayKey {
  const stamp = formatInTimeZone(instant, timeZone, 'yyyy-MM-dd HH')
  const date = stamp.slice(0, 10)
  const hour = Number(stamp.slice(11, 13))
  return hour < dayStartHour ? shiftDayKey(date, -1) : date
}

/** The Monday-based logical week a day belongs to. */
export function weekStartKey(key: DayKey): DayKey {
  return shiftDayKey(key, -weekdayOf(key))
}

/** The seven logical days of the week containing `key`, Monday first. */
export function weekDayKeys(key: DayKey): DayKey[] {
  const monday = weekStartKey(key)
  return Array.from({ length: 7 }, (_, i) => shiftDayKey(monday, i))
}

export function weekRange(
  key: DayKey,
  dayStartHour: number = DEFAULT_DAY_START_HOUR,
  timeZone: string = APP_TIME_ZONE,
): { start: Date; end: Date; days: DayKey[] } {
  const days = weekDayKeys(key)
  const first = days[0] as DayKey
  const last = days[6] as DayKey
  return {
    start: dayStartInstant(first, dayStartHour, timeZone),
    end: dayStartInstant(shiftDayKey(last, 1), dayStartHour, timeZone),
    days,
  }
}

/**
 * Position of an instant within a logical day, as a 0..1 fraction of that day's
 * real length. Timeline and week views use this so a DST day still fills its
 * column exactly, instead of overflowing or leaving a gap.
 */
export function fractionOfDay(
  instant: Date,
  key: DayKey,
  dayStartHour: number = DEFAULT_DAY_START_HOUR,
  timeZone: string = APP_TIME_ZONE,
): number {
  const { start, end } = dayRange(key, dayStartHour, timeZone)
  const span = end.getTime() - start.getTime()
  return span === 0 ? 0 : (instant.getTime() - start.getTime()) / span
}

/** Hours since the logical day start, for axis labelling. */
export function hoursIntoDay(instant: Date, key: DayKey, dayStartHour = DEFAULT_DAY_START_HOUR): number {
  return (instant.getTime() - dayStartInstant(key, dayStartHour).getTime()) / HOUR_MS
}

export function todayKey(
  dayStartHour: number = DEFAULT_DAY_START_HOUR,
  now: Date = new Date(),
): DayKey {
  return dayKeyOf(now, dayStartHour)
}
